class StickyFiltersProcessor {
    scrollContainer = null;
    filters = null;
    filtersContainer = null;
    filtersRect = null;
    filtersContainerRect = null;
    mainContent = null;
    mainContentRect = null;
    lastScrollY = 0;
    lastFiltersHeight = 0;
    handleScrollFunc = null;
    handleScrollFiltersFunc = null;
    bottomOffset = 0;
    lastDirection = null;
    isPausedStickyFilters = false;
    _currentStyles = {
        position: 'static',
    };

    get currentStyles() {
        return this._currentStyles;
    }

    set currentStyles(value) {
        this._currentStyles = value;

        Object.entries(this._currentStyles).forEach(([style, value]) => {
            this.filters.style[style] = typeof value === 'number' ? `${value}px` : value;
        });
    }

    constructor(scrollContainer, filters, filtersContainer, mainContent, root) {
        this.scrollContainer = scrollContainer;
        this.filters = filters;
        this.filtersContainer = filtersContainer;
        this.mainContent = mainContent;

        this.handleScrollFunc = this.handleScroll.bind(this);
        this.handleScrollFiltersFunc = this.handleScrollFilters.bind(this);

        this.OFFSET_BOTTOM_CONST = parseInt(root.style.getPropertyValue('--ss-results-anchor-bottom-height') || 0);

        this.scrollContainer.addEventListener('scroll', this.handleScrollFunc);

        this.filters.addEventListener('wheel', this.handleScrollFiltersFunc);

        setTimeout(() => {
            this.lastFiltersHeight = filters.clientHeight;
        });
    }

    handleFiltersSizeChange(heightOffset) {
        if (this.isPausedStickyFilters) {
            return;
        }

        const { position } = this.currentStyles;

        if (position === 'sticky') {
            const filtersRect = this.filters.getBoundingClientRect();
            const filtersContainerRect = this.filtersContainer.getBoundingClientRect();

            const offsetTop = this.getOffsetTop(filtersRect, filtersContainerRect);

            this.currentStyles = {
                position: 'relative',
                top: offsetTop,
                clipPath: 'unset',
            };
        }

        if (position === 'fixed') {
            const filtersHeight = this.filters.clientHeight + (heightOffset || 0);

            if (heightOffset !== undefined) {
                if (filtersHeight > this.scrollContainer.clientHeight) {
                    const offset = filtersHeight - this.scrollContainer.clientHeight;

                    this.currentStyles = {
                        position: 'fixed',
                        clipPath: `inset(${offset}px -20px 0px -20px)`,
                        top: 'unset',
                        bottom: this.OFFSET_BOTTOM_CONST,
                        transition: 'clip-path 0.2s ease-in-out',
                    };
                } else {
                    this.currentStyles = {
                        position: 'sticky',
                        top: 0,
                        clipPath: 'unset',
                    };
                }
            } else {
                if (filtersHeight <= this.scrollContainer.clientHeight) {
                    this.currentStyles = {
                        position: 'sticky',
                        top: 0,
                        clipPath: 'unset',
                    };
                } else {
                    const offset = this.filters.clientHeight - this.scrollContainer.clientHeight;

                    this.currentStyles = {
                        position: 'fixed',
                        clipPath: `inset(${offset}px -20px 0px -20px)`,
                    };
                }
            }
        }

        if (position === 'relative') {
            const heightDiff = this.filters.clientHeight - this.lastFiltersHeight;

            this.filtersContainer.style.minHeight = `${this.filtersContainer.clientHeight + heightDiff}px`;
        }

        this.lastFiltersHeight = this.filters.clientHeight + (heightOffset || 0);
    }

    handleScroll() {
        const currentScrollTop = this.scrollContainer.scrollTop;

        let direction = 'onTop';

        if (this.lastScrollY < currentScrollTop) {
            direction = 'onBottom';
        }

        const doFiltersFitInWindow = this.filters.clientHeight <= this.scrollContainer.clientHeight;

        if (doFiltersFitInWindow) {
            this.currentStyles = {
                position: 'sticky',
                top: 0,
                bottom: 'unset',
                clipPath: 'unset',
            };
            return;
        }

        this.filtersRect = this.filters.getBoundingClientRect();
        this.filtersContainerRect = this.filtersContainer.getBoundingClientRect();
        this.mainContentRect = this.mainContent.getBoundingClientRect();

        this.bottomOffset = this.mainContentRect.bottom - this.filtersContainerRect.bottom + this.OFFSET_BOTTOM_CONST;

        const viewportIntersections = this.calcViewportIntersections();

        const scrollData = {
            viewportIntersections,
        };

        if (direction === 'onTop') {
            this.handleScrollToTop(scrollData);
        } else {
            this.handleScrollToBottom(scrollData);
        }

        this.lastScrollY = currentScrollTop;
        this.lastDirection = direction;
    }

    handleScrollFilters(event) {
        const target = event.target.closest('.l-ss-c-filter-enum-group-wrapper-full');

        if (target) {
            return;
        }

        event.preventDefault();

        this.scrollContainer.scrollTop += event.deltaY;
    }

    calcViewportIntersections() {
        let viewportIntersectedWithBottom = false;
        let viewportIntersectedWithTop = false;

        const windowBottom = window.innerHeight;

        if (this.filtersRect.bottom + this.bottomOffset <= windowBottom) {
            viewportIntersectedWithBottom = true;
        }

        // eslint-disable-next-line no-magic-numbers
        if (this.filtersRect.top >= this.scrollContainer.offsetTop - 10) {
            viewportIntersectedWithTop = true;
        }

        return {
            viewportIntersectedWithBottom,
            viewportIntersectedWithTop,
        };
    }

    getOffsetTop(filtersRect = this.filtersRect, filtersContainerRect = this.filtersContainerRect) {
        const filtersBottom = filtersRect.bottom;
        const filtersHeight = filtersRect.height;

        return filtersBottom - filtersContainerRect.top - filtersHeight;
    }

    handleScrollToTop({
        viewportIntersections,
    }) {
        const { viewportIntersectedWithTop } = viewportIntersections;

        if (viewportIntersectedWithTop) {
            this.currentStyles = {
                position: 'sticky',
                top: 0,
                bottom: 'unset',
                clipPath: 'unset',
            };
        } else if (this.lastDirection === 'onBottom') {
            const position = this.getOffsetTop();

            this.currentStyles = {
                position: 'relative',
                top: position,
                bottom: 'unset',
                clipPath: 'unset',
            };
        }
    }

    handleScrollToBottom({
        viewportIntersections,
    }) {
        const { viewportIntersectedWithBottom } = viewportIntersections;

        if (viewportIntersectedWithBottom) {
            if (this.filtersRect.bottom >= this.filtersContainerRect.bottom) {
                let position = this.filtersContainerRect.height - this.filtersRect.height;

                this.currentStyles = {
                    position: 'relative',
                    top: position,
                    bottom: 'unset',
                    clipPath: 'unset',
                };
            } else {
                const offset = this.filtersRect.height - this.scrollContainer.clientHeight;

                this.currentStyles = {
                    position: 'fixed',
                    top: 'unset',
                    bottom: this.bottomOffset,
                    clipPath: `inset(${offset}px -20px 0px -20px)`,
                };
            }
        } else {
            const filtersPosition = this.currentStyles?.position;

            if (filtersPosition === 'sticky') {
                const position = this.getOffsetTop();

                this.currentStyles = {
                    position: 'relative',
                    top: position,
                    bottom: 'unset',
                    clipPath: 'unset',
                };
            }
        }
    }

    pause() {
        this.isPausedStickyFilters = true;

        this.destroy();

        if (this.currentStyles.position === 'sticky') {
            return;
        }

        this.filtersContainer.style.minHeight = `${this.filters.clientHeight}px`;

        this.currentStyles = {
            position: 'sticky',
            top: 0,
            bottom: 'unset',
            clipPath: 'unset',
        };
    }

    resume() {
        this.isPausedStickyFilters = false;

        this.scrollContainer.addEventListener('scroll', this.handleScrollFunc);

        this.filters.addEventListener('wheel', this.handleScrollFiltersFunc);

        this.filtersContainer.style.minHeight = 'unset';
    }

    destroy() {
        this.scrollContainer?.removeEventListener('scroll', this.handleScrollFunc);
        this.filters?.removeEventListener('wheel', this.handleScrollFiltersFunc);
    }
}

function prepareRoot(root) {
    if (root !== document) {
        return root;
    }

    const querySelector = (selectors) => {
        return document.querySelector('.l-ss-c-host-node-is-search-results').shadowRoot.querySelector(selectors);
    };

    const addEventListener = (eventName, callback) => {
        document.addEventListener(eventName, callback);
    };

    const removeEventListener = (eventName, callback) => {
        document.removeEventListener(eventName, callback);
    };

    return {
        querySelector,
        addEventListener,
        removeEventListener,
        get scrollTop() {
            return window.scrollY;
        },
        get clientHeight() {
            return window.innerHeight;
        },
        get offsetTop() {
            return 0;
        },
    };
}

export default function (root, filtersEl, mainContent) {
    const newRoot = prepareRoot(root);
    const filtersContainer = newRoot.querySelector('.l-ss-c-filters-container');
    // Const mainContent = root.querySelector('.l-ss-c-search-popup-content-wrapper');

    const stickyFiltersProcessor = new StickyFiltersProcessor(newRoot, filtersEl, filtersContainer, mainContent, root);

    return {
        onChangeFiltersSize: (event) => {
            stickyFiltersProcessor.handleFiltersSizeChange(event);
        },
        pause: () => {
            stickyFiltersProcessor.pause();
        },
        resume: () => {
            stickyFiltersProcessor.resume();
        },
    };
}

